/**
 * O seletor de visões salvas, que vive na faixa da tela.
 *
 * Mostra as visões como abas curtas, com a ativa marcada, mais um menu para
 * salvar a combinação atual, marcar como padrão e remover. Deliberadamente
 * pequeno: é atalho, não navegação.
 */

import { useState, type ReactNode } from "react"
import * as Menu from "@radix-ui/react-dropdown-menu"
import { alternarPadrao, lerVisoes, removerVisao, salvarVisao, type Visao } from "./visoes"
import { escalaFlutuante } from "./flutuante"

export function SeletorDeVisoes<F>({
  tela, escopo, filtroAtual, temFiltro, ativa, aoAplicar, aoLimpar,
}: {
  tela: string
  escopo: string | number
  filtroAtual: F
  /** Quantos campos estão preenchidos agora — decide se dá para salvar. */
  temFiltro: number
  ativa: string | null
  aoAplicar: (v: Visao<F>) => void
  aoLimpar: () => void
}) {
  const [visoes, setVisoes] = useState<Visao<F>[]>(() => lerVisoes<F>(tela, escopo))
  const [nome, setNome] = useState("")
  const [abrindo, setAbrindo] = useState(false)

  function salvar() {
    if (!nome.trim()) return
    setVisoes(salvarVisao(tela, escopo, nome, filtroAtual))
    setNome("")
    setAbrindo(false)
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <Aba ligada={ativa === null} aoClicar={aoLimpar}>Tudo</Aba>

      {visoes.map((v) => (
        <Aba key={v.id} ligada={ativa === v.id} aoClicar={() => aoAplicar(v)}>
          {v.padrao && (
            <svg width="9" height="9" viewBox="0 0 24 24" aria-label="padrão" role="img"
                 style={{ fill: "currentColor", marginRight: 4, flex: "0 0 9px" }}>
              <path d="m12 3 2.6 5.8 6.4.7-4.8 4.3 1.3 6.2L12 17l-5.5 3 1.3-6.2L3 9.5l6.4-.7L12 3Z" />
            </svg>
          )}
          {v.nome}
        </Aba>
      ))}

      <Menu.Root open={abrindo} onOpenChange={setAbrindo}>
        <Menu.Trigger
          aria-label="Visões salvas"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 24, height: 24, borderRadius: 6, color: "var(--txt3)",
            border: "1px dashed var(--line2)",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }}>
            <path d="M12 5.5v13M5.5 12h13" />
          </svg>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content
            sideOffset={6} align="start"
            onCloseAutoFocus={(e) => e.preventDefault()}
            style={{
              minWidth: 250, padding: 8, borderRadius: 10, background: "var(--surface)",
              border: "1px solid var(--line2)", boxShadow: "var(--sombra)", color: "var(--txt)", zIndex: 50,
              ...escalaFlutuante,
            }}
          >
            <Rotulo>Salvar a combinação de agora</Rotulo>

            {temFiltro === 0 ? (
              <p style={{ margin: "2px 2px 6px", fontSize: 11.5, color: "var(--txt3)", lineHeight: 1.4 }}>
                Nenhum filtro aplicado. Filtre alguma coisa antes de salvar — visão sem filtro é a
                aba “Tudo”, que já existe.
              </p>
            ) : (
              <div style={{ display: "flex", gap: 6, padding: "2px 2px 6px" }}>
                <input
                  value={nome}
                  autoFocus
                  onChange={(e) => setNome(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                    if (e.key === "Enter") salvar()
                  }}
                  placeholder={`Nome (${temFiltro} ${temFiltro === 1 ? "filtro" : "filtros"})`}
                  style={{
                    flex: 1, minWidth: 0, height: 28, padding: "0 9px", borderRadius: 7,
                    border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
                  }}
                />
                <button
                  onClick={salvar}
                  disabled={!nome.trim()}
                  style={{
                    height: 28, padding: "0 11px", borderRadius: 7, background: "var(--brand)",
                    color: "#FFFFFF", fontSize: 12, fontWeight: 600,
                    opacity: nome.trim() ? 1 : 0.5,
                  }}
                >
                  Salvar
                </button>
              </div>
            )}

            {visoes.length > 0 && (
              <>
                <Menu.Separator style={{ height: 1, background: "var(--line)", margin: "6px 0" }} />
                <Rotulo>Salvas nesta máquina</Rotulo>
                {visoes.map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 6, padding: "4px 2px",
                      fontSize: 12.5,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.nome}
                    </span>
                    <Acao
                      titulo={v.padrao ? "Deixar de abrir por padrão" : "Abrir esta por padrão"}
                      ligada={!!v.padrao}
                      aoClicar={() => setVisoes(alternarPadrao(tela, escopo, v.id))}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                           style={{ fill: v.padrao ? "currentColor" : "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinejoin: "round" }}>
                        <path d="m12 3 2.6 5.8 6.4.7-4.8 4.3 1.3 6.2L12 17l-5.5 3 1.3-6.2L3 9.5l6.4-.7L12 3Z" />
                      </svg>
                    </Acao>
                    <Acao titulo="Remover esta visão" aoClicar={() => setVisoes(removerVisao(tela, escopo, v.id))}>
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                           style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" }}>
                        <path d="M6 6l12 12M18 6 6 18" />
                      </svg>
                    </Acao>
                  </div>
                ))}
              </>
            )}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
    </div>
  )
}

function Aba({ ligada, aoClicar, children }: { ligada: boolean; aoClicar: () => void; children: ReactNode }) {
  return (
    <button
      onClick={aoClicar}
      aria-pressed={ligada}
      style={{
        display: "inline-flex", alignItems: "center", height: 24, padding: "0 10px",
        borderRadius: 6, fontSize: 12, fontWeight: ligada ? 600 : 500, maxWidth: 170,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        background: ligada ? "var(--brand-soft)" : "transparent",
        color: ligada ? "var(--brand-on-soft)" : "var(--txt2)",
        border: `1px solid ${ligada ? "var(--brand)" : "transparent"}`,
      }}
    >
      {children}
    </button>
  )
}

function Acao({ titulo, ligada, aoClicar, children }: {
  titulo: string; ligada?: boolean; aoClicar: () => void; children: ReactNode
}) {
  return (
    <button
      title={titulo}
      aria-label={titulo}
      onClick={aoClicar}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, flex: "0 0 22px",
        color: ligada ? "var(--acao)" : "var(--txt3)",
      }}
    >
      {children}
    </button>
  )
}

function Rotulo({ children }: { children: ReactNode }) {
  return (
    <div style={{
      margin: "0 0 4px", padding: "2px 2px", fontSize: 10, fontWeight: 600, letterSpacing: ".06em",
      textTransform: "uppercase", color: "var(--acao)",
    }}>
      {children}
    </div>
  )
}
